import { useRef } from 'react';
import type { DocumentReviewStatus } from '../../../../mocks/cases';
import CategoryHeader from './CategoryHeader';
import type { DocState } from './types';
import { randomSize, reviewStatusBadgeCls, reviewStatusLabel } from './utils';
import { DOCUMENT_FILE_ACCEPT } from '../../../../utils/documentUploadPolicy';

interface Props {
  docs: DocState[];
  onDocsChange: (next: DocState[]) => void;
  categoryReview: DocumentReviewStatus;
  onCategoryReviewChange: (status: DocumentReviewStatus) => void;
  onViewDocument?: (doc: DocState) => void;
}

export default function VulnerabilityCategory({ docs, onDocsChange, categoryReview, onCategoryReviewChange, onViewDocument }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);

  const vulnDoc = docs.find((d) => d.category === 'vulnerability' && d.uploaded);

  return (
    <div className="max-w-[480px]">
      <CategoryHeader title="Vulnerability documentation" category="vulnerability" status={categoryReview} onChange={onCategoryReviewChange} />

      {vulnDoc ? (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex flex-col gap-2 mb-2 min-[420px]:flex-row min-[420px]:items-center">
          <i className="ri-file-check-line text-emerald-500 text-sm shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="min-w-0 text-[12px] font-medium text-emerald-800 truncate">{vulnDoc.uploadedFile}</p>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusBadgeCls(vulnDoc.reviewStatus)}`}>
                {reviewStatusLabel(vulnDoc.reviewStatus)}
              </span>
            </div>
            {vulnDoc.fileSize && <p className="text-[11px] text-emerald-600">{vulnDoc.fileSize}</p>}
          </div>
          <button type="button" onClick={() => onViewDocument?.(vulnDoc)}
            className="min-h-[36px] text-[11px] text-slate-600 font-medium border border-slate-200 rounded-md px-2 py-1 cursor-pointer hover:bg-slate-50 shrink-0 whitespace-nowrap bg-white">View</button>
          <button type="button" onClick={() => fileRef.current?.click()}
            className="min-h-[36px] text-[11px] text-emerald-600 font-medium underline cursor-pointer hover:text-emerald-700 shrink-0 whitespace-nowrap">Change</button>
          <button type="button" onClick={() => onDocsChange(docs.filter((d) => d.id !== vulnDoc.id))}
            className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-rose-500 cursor-pointer shrink-0">
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      ) : (
        <div className="flex">
          <button type="button" onClick={() => fileRef.current?.click()}
            className="w-full min-h-[44px] bg-white border border-slate-200 rounded-lg flex items-center justify-center gap-2 text-sm text-slate-600 hover:border-slate-300 hover:bg-slate-50 cursor-pointer whitespace-nowrap">
            <i className="ri-file-upload-line" /> Choose file
          </button>
        </div>
      )}

      <input ref={fileRef} type="file" accept={DOCUMENT_FILE_ACCEPT} className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (vulnDoc) {
            onDocsChange(docs.map((d) => d.id === vulnDoc.id ? { ...d, uploadedFile: file.name, fileSize: randomSize(), rawFile: file } : d));
          } else {
            onDocsChange([...docs, {
              id: `vul-${Date.now()}`, name: 'Anexo II',
              category: 'vulnerability', docType: 'anexoII',
              uploaded: true, uploadedAt: new Date().toISOString().slice(0, 10),
              uploadedFile: file.name, rawFile: file, reviewStatus: 'pending' as DocumentReviewStatus,
              fileSize: randomSize(),
            }]);
          }
          e.target.value = '';
        }} />
    </div>
  );
}
